/* ─────────────────────────────────────────────────────────
 *  MIDI DAW – Polyphonic Synth Engine
 *
 *  Web Audio API synth with ADSR envelope, biquad filter,
 *  stereo chorus, and convolution reverb. Supports pad,
 *  lead, string (Karplus-Strong), and standard oscillators.
 *
 *  Designed for MIDI playback — schedule noteOn/noteOff
 *  at precise audioContext timestamps.
 * ────────────────────────────────────────────────────────── */
import type { DawSynthPatch } from '../types/midi-daw-types'
import { midiToFrequency } from '../types/midi-daw-types'

// ── Voice (single playing note) ────────────────────────────

interface Voice {
  osc: OscillatorNode | AudioBufferSourceNode
  oscB?: OscillatorNode                       // detuned layer for pad/lead
  gain: GainNode
  filter?: BiquadFilterNode
  revGain?: GainNode
  stopTime: number
}

// ── Synth Engine ───────────────────────────────────────────

export class DawSynthEngine {
  private static MAX_POLYPHONY = 64
  private ctx: AudioContext
  private masterGain: GainNode
  private reverbSend: GainNode
  private reverbReturn: ConvolverNode | null = null
  private dryBus: GainNode
  private voices = new Map<string, Voice>()
  private scheduledSources = new Set<OscillatorNode | AudioBufferSourceNode>()
  private activeVoices = 0

  constructor(ctx: AudioContext) {
    this.ctx = ctx

    // Master output chain: dry + reverb → master → destination
    this.masterGain = ctx.createGain()
    this.masterGain.gain.value = 0.6
    this.masterGain.connect(ctx.destination)

    this.dryBus = ctx.createGain()
    this.dryBus.gain.value = 1
    this.dryBus.connect(this.masterGain)

    this.reverbSend = ctx.createGain()
    this.reverbSend.gain.value = 0.3
    this.reverbSend.connect(this.masterGain)

    this.buildReverb()
  }

  /** Set master volume 0–1 */
  setMasterVolume(v: number) {
    this.masterGain.gain.setValueAtTime(
      Math.max(0, Math.min(1, v)),
      this.ctx.currentTime,
    )
  }

  /** Schedule a note-on at a precise audio time */
  noteOn(
    midi: number,
    velocity: number,
    startTime: number,
    patch: DawSynthPatch,
    trackVolume: number,
    voiceKey: string,
  ) {
    this.noteOff(voiceKey) // kill previous if still ringing

    const freq = midiToFrequency(midi)
    const vel = (velocity / 127) * trackVolume
    const { attack, decay, sustain, release } = patch

    // ── Gain envelope ──────────────────
    const gain = this.ctx.createGain()
    gain.gain.setValueAtTime(0, startTime)
    gain.gain.linearRampToValueAtTime(vel, startTime + attack)
    gain.gain.linearRampToValueAtTime(vel * sustain, startTime + attack + decay)

    // ── Optional filter ────────────────
    let filter: BiquadFilterNode | undefined
    if (patch.filterCutoff > 0) {
      filter = this.ctx.createBiquadFilter()
      filter.type = 'lowpass'
      filter.frequency.value = patch.filterCutoff
      filter.Q.value = patch.filterQ
      filter.connect(gain)
    }

    const target = filter ?? gain

    // ── Route to dry + reverb buses ────
    gain.connect(this.dryBus)
    let revGain: GainNode | undefined
    if (patch.reverbMix > 0) {
      revGain = this.ctx.createGain()
      revGain.gain.value = patch.reverbMix
      gain.connect(revGain)
      revGain.connect(this.reverbSend)
    }

    // ── Oscillator(s) ──────────────────
    let osc: OscillatorNode | AudioBufferSourceNode
    let oscB: OscillatorNode | undefined

    if (patch.type === 'string') {
      // Karplus-Strong string synthesis
      osc = this.createKarplusStrong(freq, startTime)
      osc.connect(target)
    } else {
      const oscType = this.resolveOscType(patch.type)
      const primary = this.ctx.createOscillator()
      primary.type = oscType
      primary.frequency.setValueAtTime(freq, startTime)
      primary.connect(target)
      osc = primary

      // Detuned second oscillator for pad/lead width
      if (patch.detune !== 0 && (patch.type === 'pad' || patch.type === 'lead' || patch.type === 'sawtooth')) {
        oscB = this.ctx.createOscillator()
        oscB.type = oscType
        oscB.frequency.setValueAtTime(freq, startTime)
        oscB.detune.setValueAtTime(patch.detune, startTime)
        oscB.connect(target)
        oscB.start(startTime)
      }

      primary.start(startTime)
    }

    this.voices.set(voiceKey, { osc, oscB, gain, filter, revGain, stopTime: 0 })
  }

  /** Schedule a note-off (ADSR release phase) */
  noteOff(voiceKey: string, offTime?: number) {
    const voice = this.voices.get(voiceKey)
    if (!voice) return

    const t = offTime ?? this.ctx.currentTime
    const release = 0.3 // default release

    voice.gain.gain.cancelScheduledValues(t)
    voice.gain.gain.setValueAtTime(voice.gain.gain.value, t)
    voice.gain.gain.linearRampToValueAtTime(0, t + release)

    const stopAt = t + release + 0.05

    if (voice.osc instanceof OscillatorNode) {
      voice.osc.stop(stopAt)
    } else {
      voice.osc.stop(stopAt)
    }
    voice.oscB?.stop(stopAt)

    // Disconnect all nodes after release tail ends
    const cleanup = () => {
      voice.gain.disconnect()
      voice.filter?.disconnect()
      voice.revGain?.disconnect()
      voice.oscB?.disconnect()
    }
    if (voice.osc instanceof OscillatorNode) {
      voice.osc.onended = cleanup
    } else {
      voice.osc.onended = cleanup
    }

    voice.stopTime = stopAt
    this.voices.delete(voiceKey)
  }

  /** Schedule a complete note (on + off) — for MIDI playback */
  scheduleNote(
    midi: number,
    velocity: number,
    startTime: number,
    duration: number,
    patch: DawSynthPatch,
    trackVolume: number,
    voiceKey: string,
  ) {
    // ── Polyphony cap — skip if we're already at the limit ──
    if (this.activeVoices >= DawSynthEngine.MAX_POLYPHONY) return
    this.activeVoices++

    const freq = midiToFrequency(midi)
    const vel = (velocity / 127) * trackVolume
    const { attack, decay, sustain, release } = patch

    // ── Gain with full ADSR ────────────
    const gain = this.ctx.createGain()
    gain.gain.setValueAtTime(0, startTime)
    gain.gain.linearRampToValueAtTime(vel, startTime + attack)
    gain.gain.linearRampToValueAtTime(vel * sustain, startTime + attack + decay)
    // Hold sustain until noteOff
    const offTime = startTime + duration
    gain.gain.setValueAtTime(vel * sustain, offTime)
    gain.gain.linearRampToValueAtTime(0, offTime + release)

    // ── Filter ─────────────────────────
    let filter: BiquadFilterNode | undefined
    if (patch.filterCutoff > 0) {
      filter = this.ctx.createBiquadFilter()
      filter.type = 'lowpass'
      filter.frequency.value = patch.filterCutoff
      filter.Q.value = patch.filterQ
      filter.connect(gain)
    }

    const target = filter ?? gain

    // ── Routing ────────────────────────
    gain.connect(this.dryBus)
    let revGain: GainNode | undefined
    if (patch.reverbMix > 0) {
      revGain = this.ctx.createGain()
      revGain.gain.value = patch.reverbMix
      gain.connect(revGain)
      revGain.connect(this.reverbSend)
    }

    // ── Cleanup: disconnect all nodes when the primary source ends ──
    const cleanup = () => {
      gain.disconnect()
      filter?.disconnect()
      revGain?.disconnect()
      this.activeVoices--
    }

    // ── Oscillator ─────────────────────
    const stopAt = offTime + release + 0.05

    if (patch.type === 'string') {
      const src = this.createKarplusStrong(freq, startTime)
      src.connect(target)
      src.stop(stopAt)
      this.scheduledSources.add(src)
      src.onended = () => { this.scheduledSources.delete(src); cleanup() }
    } else {
      const oscType = this.resolveOscType(patch.type)
      const primary = this.ctx.createOscillator()
      primary.type = oscType
      primary.frequency.setValueAtTime(freq, startTime)
      primary.connect(target)
      primary.start(startTime)
      primary.stop(stopAt)
      this.scheduledSources.add(primary)
      primary.onended = () => { this.scheduledSources.delete(primary); cleanup() }

      if (patch.detune !== 0 && (patch.type === 'pad' || patch.type === 'lead' || patch.type === 'sawtooth')) {
        const b = this.ctx.createOscillator()
        b.type = oscType
        b.frequency.setValueAtTime(freq, startTime)
        b.detune.setValueAtTime(patch.detune, startTime)
        b.connect(target)
        b.start(startTime)
        b.stop(stopAt)
        this.scheduledSources.add(b)
        b.onended = () => { this.scheduledSources.delete(b) }
      }
    }
  }

  /** Stop all voices immediately */
  panic() {
    const keys = Array.from(this.voices.keys())
    keys.forEach(key => this.noteOff(key, this.ctx.currentTime))
    this.voices.clear()

    // Kill pre-scheduled oscillators from scheduleNote()
    const now = this.ctx.currentTime
    this.scheduledSources.forEach(src => {
      try { src.stop(now) } catch { /* already stopped */ }
    })
    this.scheduledSources.clear()
    this.activeVoices = 0
  }

  /**
   * Retune a sustaining voice in place to a new MIDI pitch.
   * Non-destructive: preserves the running ADSR/gain/filter chain,
   * so the user perceives a seamless glide (or snap) rather than
   * a re-attack. Karplus-Strong voices cannot be retuned (the
   * delay line is fixed at noteOn) and are re-triggered via the
   * `onRetrigger` fallback callback, if supplied.
   *
   * @returns true if retuned in place, false if re-trigger needed.
   */
  setVoicePitch(
    voiceKey: string,
    midi: number,
    glideSeconds = 0.015,
  ): boolean {
    const voice = this.voices.get(voiceKey)
    if (!voice) return false

    // Karplus-Strong (string) uses a fixed-length delay line — we
    // cannot retune the fundamental without rebuilding the buffer.
    if (!(voice.osc instanceof OscillatorNode)) return false

    const freq = midiToFrequency(midi)
    const t = this.ctx.currentTime
    const glideTo = t + Math.max(0, glideSeconds)

    voice.osc.frequency.cancelScheduledValues(t)
    voice.osc.frequency.setValueAtTime(voice.osc.frequency.value, t)
    voice.osc.frequency.linearRampToValueAtTime(freq, glideTo)

    if (voice.oscB) {
      voice.oscB.frequency.cancelScheduledValues(t)
      voice.oscB.frequency.setValueAtTime(voice.oscB.frequency.value, t)
      voice.oscB.frequency.linearRampToValueAtTime(freq, glideTo)
    }
    return true
  }

  /** Returns true if a voice with this key is currently held. */
  hasVoice(voiceKey: string): boolean {
    return this.voices.has(voiceKey)
  }

  /** Dispose engine */
  dispose() {
    this.panic()
    this.masterGain.disconnect()
  }

  // ── Private helpers ──────────────────────────────────────

  private resolveOscType(type: string): OscillatorType {
    switch (type) {
      case 'pad':      return 'sawtooth'
      case 'lead':     return 'sawtooth'
      case 'sawtooth': return 'sawtooth'
      case 'square':   return 'square'
      case 'triangle': return 'triangle'
      default:         return 'sine'
    }
  }

  private createKarplusStrong(freq: number, startTime: number): AudioBufferSourceNode {
    const period = Math.round(this.ctx.sampleRate / freq)
    const bufferLength = Math.round(this.ctx.sampleRate * 0.5) // 0.5s — enough for MIDI string decay
    const buffer = this.ctx.createBuffer(1, bufferLength, this.ctx.sampleRate)
    const data = buffer.getChannelData(0)

    // Initial burst — random noise in first period
    for (let i = 0; i < period; i++) {
      data[i] = Math.random() * 2 - 1
    }
    // Averaging filter (Karplus-Strong)
    for (let i = period; i < bufferLength; i++) {
      data[i] = (data[i - period] + data[i - period + 1]) * 0.498
    }

    const src = this.ctx.createBufferSource()
    src.buffer = buffer
    src.start(startTime)
    return src
  }

  private buildReverb() {
    // Algorithmic reverb impulse (exponential decay noise)
    const sampleRate = this.ctx.sampleRate
    const length = sampleRate * 1.5 // 1.5s reverb tail
    const impulse = this.ctx.createBuffer(2, length, sampleRate)
    for (let ch = 0; ch < 2; ch++) {
      const data = impulse.getChannelData(ch)
      for (let i = 0; i < length; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (sampleRate * 0.4))
      }
    }

    this.reverbReturn = this.ctx.createConvolver()
    this.reverbReturn.buffer = impulse
    this.reverbSend.disconnect()
    this.reverbSend.connect(this.reverbReturn)
    this.reverbReturn.connect(this.masterGain)
  }
}
